import React, { useState } from "react";
import { Layout } from "@/components/layout";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Link, useLocation } from "wouter";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useLoading } from "@/providers/loading-provider";
import { Wallet, Plus, RefreshCw, Search, Filter, Loader2, Edit, MoreHorizontal } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useCurrentBusiness } from "@/hooks/use-current-business";
import { SubscriptionRequired } from "@/components/ui/subscription-required";
import { Input } from "@/components/ui/input";
import { 
  Select,
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

// Define a transaction type
interface Transaction {
  id: number;
  businessId: number;
  date: string;
  description: string;
  amount: string;
  type: string;
  status: string;
  categoryId: number | null;
  metadata: {
    paymentMethod?: string;
    vendor?: string;
    notes?: string;
  } | null;
  createdAt: string;
  updatedAt: string;
}

interface TransactionCategory {
  id: number;
  name: string;
  description: string;
  color: string;
}

export default function ExpensesPage() {
  const { toast } = useToast();
  const { currentBusiness: selectedBusiness } = useCurrentBusiness();
  const queryClient = useQueryClient();
  const { navigateTo, startLoading } = useLoading();
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");

  // Query to fetch transactions
  const { data: transactionsData, isLoading: isLoadingTransactions, error: transactionsError } = useQuery<{ transactions: Transaction[] }>({
    queryKey: [`/api/businesses/${selectedBusiness?.id}/transactions`],
    enabled: !!selectedBusiness,
  });

  // Query to fetch transaction categories
  const { data: categoriesData, isLoading: isLoadingCategories } = useQuery<TransactionCategory[]>({
    queryKey: ["/api/transaction-categories"],
    enabled: !!selectedBusiness,
  });

  // Function to refresh transactions
  const refreshTransactions = () => {
    if (selectedBusiness) {
      queryClient.invalidateQueries({ queryKey: [`/api/businesses/${selectedBusiness.id}/transactions`] });
      toast({
        title: "Refreshed",
        description: "Expense list has been refreshed",
      });
    }
  };

  // Get the category name for a given category ID
  const getCategoryName = (categoryId: number | null) => {
    if (!categoryId) return "Uncategorized";
    if (!categoriesData) return "Loading...";
    
    // Make sure categoriesData is an array before using find
    if (Array.isArray(categoriesData)) {
      // Convert categoryId to number to ensure proper type comparison
      const categoryIdNum = Number(categoryId);
      
      // Find the category by ID
      const category = categoriesData.find((cat: TransactionCategory) => cat.id === categoryIdNum);
      
      // Return the category name if found, otherwise return uncategorized
      if (category) {
        return category.name;
      } else {
        console.log(`Category ID ${categoryIdNum} not found in available categories`);
        return "Uncategorized";
      }
    } else {
      return "Uncategorized";
    }
  };

  // Format date for display
  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), "MMM d, yyyy");
    } catch (error) {
      return "Invalid date";
    }
  };

  // Filter transactions
  const getFilteredTransactions = () => {
    if (!transactionsData?.transactions) return [];
    
    return transactionsData.transactions
      .filter((transaction: Transaction) => 
        transaction.type === "expense" &&
        (searchTerm === "" || 
          transaction.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (transaction.metadata?.vendor || "").toLowerCase().includes(searchTerm.toLowerCase())
        ) &&
        (categoryFilter === "" || ((transaction as any).coaAccountId || (transaction as any).coa_account_id || transaction.categoryId) === parseInt(categoryFilter))
      )
      .sort((a: Transaction, b: Transaction) => 
        new Date(b.date).getTime() - new Date(a.date).getTime()
      );
  };

  if (!selectedBusiness) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh] flex-col">
          <Wallet className="h-16 w-16 text-gray-300 mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">No Business Selected</h2>
          <p className="text-gray-600 mb-4">Please select or create a business to manage expenses.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      
      <div className="px-4 py-6 sm:px-6 lg:px-8">
        {/* Page header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-neutral-900">Expenses</h1>
            <p className="mt-1 text-sm text-neutral-500">
              Track and manage all your business expenses
            </p>
          </div>
          <div className="mt-4 md:mt-0 flex space-x-3">
            <Button variant="outline">
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
            <Button asChild>
              <Link href="/expenses/create">
                <Plus className="h-4 w-4 mr-2" />
                New Expense
              </Link>
            </Button>
          </div>
        </div>

        {/* Search and filter bar */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="relative col-span-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search expenses..."
              className="pl-10"
            />
          </div>
          <div className="col-span-1">
            <Select>
              <SelectTrigger>
                <SelectValue placeholder="Filter by category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                <SelectItem value="office">Office Supplies</SelectItem>
                <SelectItem value="travel">Travel</SelectItem>
                <SelectItem value="meals">Meals & Entertainment</SelectItem>
                <SelectItem value="utilities">Utilities</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Expenses tabs */}
        <Tabs defaultValue="all" className="mt-6">
          <TabsList className="mb-6">
            <TabsTrigger value="all">All Expenses</TabsTrigger>
            <TabsTrigger value="recent">Recent</TabsTrigger>
            <TabsTrigger value="categories">By Category</TabsTrigger>
            <TabsTrigger value="reimbursable">Reimbursable</TabsTrigger>
          </TabsList>
          
          <TabsContent value="all">
            <Card>
              <CardHeader>
                <CardTitle>All Expenses</CardTitle>
                <CardDescription>View and manage all your expenses</CardDescription>
              </CardHeader>
              <CardContent>
                {isLoadingTransactions ? (
                  <div className="flex items-center justify-center h-64">
                    <div className="text-center">
                      <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
                      <p className="text-gray-500">Loading expenses...</p>
                    </div>
                  </div>
                ) : transactionsError ? (
                  <div className="flex items-center justify-center h-64 border border-dashed rounded-lg">
                    <div className="text-center">
                      <p className="text-red-500 mb-2">Error loading expenses</p>
                      <Button variant="outline" onClick={refreshTransactions}>
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Try Again
                      </Button>
                    </div>
                  </div>
                ) : getFilteredTransactions().length === 0 ? (
                  <div className="flex items-center justify-center h-64 border border-dashed rounded-lg">
                    <div className="text-center">
                      <Wallet className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-900">No expenses recorded</h3>
                      <p className="mt-1 text-sm text-gray-500">
                        Start tracking your business expenses by adding your first expense
                      </p>
                      <div className="mt-6">
                        <Button asChild>
                          <Link href="/expenses/create">
                            <Plus className="h-4 w-4 mr-2" />
                            New Expense
                          </Link>
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead>Vendor</TableHead>
                        <TableHead>Method</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {getFilteredTransactions().map((transaction: Transaction) => (
                        <TableRow key={transaction.id}>
                          <TableCell>{formatDate(transaction.date)}</TableCell>
                          <TableCell className="font-medium">{transaction.description}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className="bg-slate-50">
                              {getCategoryName((transaction as any).coaAccountId || (transaction as any).coa_account_id || transaction.categoryId)}
                            </Badge>
                          </TableCell>
                          <TableCell>{transaction.metadata?.vendor || "-"}</TableCell>
                          <TableCell>{transaction.metadata?.paymentMethod || "-"}</TableCell>
                          <TableCell className="text-right font-medium">
                            ${parseFloat(transaction.amount).toFixed(2)}
                          </TableCell>
                          <TableCell className="text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" className="h-8 w-8 p-0">
                                  <span className="sr-only">Open menu</span>
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuLabel>Actions</DropdownMenuLabel>
                                <DropdownMenuItem
                                  onClick={() => {
                                    // Use navigateTo from LoadingProvider for proper loading state
                                    navigateTo(`/expenses/edit/${transaction.id}`);
                                  }}
                                >
                                  <Edit className="mr-2 h-4 w-4" />
                                  Edit
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  className="text-red-600"
                                  onClick={() => {
                                    // This will be implemented later
                                    toast({
                                      title: "Coming Soon",
                                      description: "Delete functionality will be available soon.",
                                    });
                                  }}
                                >
                                  Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Other tab contents */}
          <TabsContent value="recent">
            <Card>
              <CardHeader>
                <CardTitle>Recent Expenses</CardTitle>
                <CardDescription>Expenses from the last 30 days</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-center h-64 border border-dashed rounded-lg">
                  <div className="text-center">
                    <p className="text-gray-500">No recent expenses found</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Similar for other tabs */}
        </Tabs>
      </div>
    </Layout>
  );
}
